<template>
	<div class="pa-3 full-height new-back">

		<billables-nest-toolbar
			v-model="filters.search"
			placeholder="Search"
			:applied-filters="appliedFilters"
			@input="handleSearch"
			@excel="onExcelExport"
		>
			<template #actions-extra>
				<v-btn
					v-if="$security.can('add')"
					class="bnt-add-billable text-none"
					color="primary"
					depressed
					outlined
					to="/admin/billable/new"
				>
					<v-icon left small>mdi-plus</v-icon>
					New Billable
				</v-btn>
			</template>
			<v-select
				v-model="filters.status" :items="statuses"
				@change="fetchBillables"
				label="BillableStatus" item-value="id" item-text="id"
				dense outlined hide-details clearable input-select multiple
				:menu-props="filterMenuProps"
			>
				<template #item="{ item, on, attrs }">
					<v-list-item v-bind="attrs" v-on="on" class="bnt-status-item">
						<v-list-item-content class="py-1">
							<v-btn
								:color="item.color"
								outlined small style="border-radius: 20px"
								:style="{
									background: $vuetify.theme.isDark ? '' : item.lightColor
								}"
							>{{item.description}}</v-btn>
						</v-list-item-content>
						<v-list-item-action class="my-0 ml-2">
							<v-icon
								v-if="(filters.status || []).includes(item.id)"
								small
								color="primary"
							>mdi-check</v-icon>
						</v-list-item-action>
					</v-list-item>
				</template>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ statusSelectionLabel }}</span>
				</template>
			</v-select>
			<v-autocomplete
				v-model="filters.customer" :items="customers"
				@change="changeCustomer"
				label="Customer"
				dense outlined hide-details clearable multiple
				item-text="name" item-value="id"
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiSelectionLabel(filters.customer, 'customer') }}</span>
				</template>
			</v-autocomplete>
			<v-autocomplete
				v-model="filters.project" :items="projects"
				@change="fetchBillables"
				label="Project"
				dense outlined hide-details clearable multiple
				item-text="name" item-value="id"
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiSelectionLabel(filters.project, 'project') }}</span>
				</template>
			</v-autocomplete>
			<v-select
				v-model="filters.currency" :items="currencies"
				@change="fetchBillables"
				label="Currency"
				dense outlined hide-details clearable input-select multiple
				item-text="text" item-value="text"
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiSelectionLabel(filters.currency, 'currency') }}</span>
				</template>
			</v-select>
			<v-select
				v-model="filters.type" :items="types"
				@change="fetchBillables"
				label="Billable Type"
				dense outlined hide-details clearable input-select multiple
				item-text="text" item-value="value"
				:parent-shown-items="billables"
				:data-comparer="(item, value) => item.type == value"
				:menu-props="filterMenuProps"
			>
				<template #selection="{ index }">
					<span v-if="index === 0" class="bnt-select-summary">{{ multiSelectionLabel(filters.type, 'type') }}</span>
				</template>
			</v-select>
			<v-select
				v-model="filters.openBillable" :items="openBillables"
				@change="fetchBillables"
				label="Billables"
				dense outlined hide-details clearable
				item-text="text" item-value="value"
				:menu-props="filterMenuProps"
			/>
			<template #filters-secondary>
				<v-autocomplete
					v-model="filters.billableCode" :items="billables"
					label="Billable Code"
					dense outlined hide-details clearable multiple
					item-text="code" item-value="id"
					:menu-props="filterMenuProps"
				>
					<template #selection="{ index }">
						<span v-if="index === 0" class="bnt-select-summary">{{ multiSelectionLabel(filters.billableCode, 'billableCode') }}</span>
					</template>
				</v-autocomplete>
				<v-autocomplete
					v-model="filters.billableItemCode" :items="billableItems"
					label="Billable Item Code"
					dense outlined hide-details clearable multiple
					item-text="code" item-value="id"
					:menu-props="filterMenuProps"
				>
					<template #selection="{ index }">
						<span v-if="index === 0" class="bnt-select-summary">{{ multiSelectionLabel(filters.billableItemCode, 'billableItemCode') }}</span>
					</template>
				</v-autocomplete>
				<v-select
					v-model="filters.billableItemStatus" :items="billableItemStatuses"
					label="Billable Item Status"
					item-value="id" item-text="id"
					dense outlined hide-details clearable input-select multiple
					:menu-props="filterMenuProps"
				>
					<template #item="{ item, on, attrs }">
						<v-list-item v-bind="attrs" v-on="on" class="bnt-status-item">
							<v-list-item-content class="py-1">
								<v-btn
									:color="item.color"
									outlined small style="border-radius: 20px"
									:style="{
										background: $vuetify.theme.isDark ? '' : item.lightColor
									}"
								>{{item.description}}</v-btn>
							</v-list-item-content>
							<v-list-item-action class="my-0 ml-2">
								<v-icon
									v-if="(filters.billableItemStatus || []).includes(item.id)"
									small
									color="primary"
								>mdi-check</v-icon>
							</v-list-item-action>
						</v-list-item>
					</template>
					<template #selection="{ index }">
						<span v-if="index === 0" class="bnt-select-summary">{{ billableItemStatusSelectionLabel }}</span>
					</template>
				</v-select>
			</template>
		</billables-nest-toolbar>

		<div v-if="!filteredBillables.length" class="rounded-lg bordered new-back lighten2 pa-6 text-center text--secondary">
			No billables found
		</div>

		<div
			v-for="section in listSections"
			:key="section.key"
			class="bi-section"
			:class="{ 'bi-section--unassigned': section.isUnassigned }"
		>
			<div v-if="section.title" class="bi-section__header">
				<span class="bi-section__title">{{ section.title }}</span>
				<v-btn color="primary" icon x-small style="background: var(--v-newDesignBackground-lighten);">
					<v-icon small color="primary" class="bi-section__icon">mdi-account-remove</v-icon>
				</v-btn>
				<div class="bi-section__line"/>
			</div>

			<div
				v-for="group in section.groups"
				:key="group.key"
				class="customer-group rounded-lg bordered new-back lighten2 mb-4"
			>
				<div class="customer-group__header">
					<span class="customer-group__title">{{ group.name }}</span>
				</div>

				<div class="customer-group__body">
					<div class="bi-row bi-header">
						<div class="bi-cell bi-header__cell bi-header__cell--expand"/>
						<div class="bi-cell bi-header__cell">Billable</div>
						<div class="bi-cell bi-header__cell">Project</div>
						<div class="bi-cell bi-header__cell">Start Date</div>
						<div class="bi-cell bi-header__cell">Type</div>
						<div class="bi-cell bi-header__cell">Status</div>
						<div class="bi-cell bi-header__cell">Currency</div>
						<div class="bi-cell bi-header__cell">Contracted</div>
						<div class="bi-cell bi-header__cell">
							<v-tooltip bottom z-index="300">
								<template #activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" class="d-inline-flex align-center">
										Assigned
										<v-icon x-small class="ml-1">mdi-information-outline</v-icon>
									</span>
								</template>
								<span>Sum of Billable Assigned Hours</span>
							</v-tooltip>
						</div>
						<div class="bi-cell bi-header__cell">
							<v-tooltip bottom z-index="300">
								<template #activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" class="d-inline-flex align-center">
										Executed
										<v-icon x-small class="ml-1">mdi-information-outline</v-icon>
									</span>
								</template>
								<span>Sum of Billable Executed Hours</span>
							</v-tooltip>
						</div>
						<div class="bi-cell bi-header__cell">Billed</div>
						<div class="bi-cell bi-header__cell bi-header__cell--actions">Actions</div>
					</div>
					<div
						v-for="item in group.items"
						:key="item.id"
						class="bi-billable-block"
					>
					<div
						class="bi-row my-1"
						:class="{ 'bi-row--clickable': $security.can('detail') }"
						@click="$security.can('detail') ? $q.goTo('/admin/billable/details/' + item.id, $event) : null"
					>
						<div class="bi-cell bi-cell--expand" @click.stop>
							<v-btn icon x-small @click="toggleExpand(item.id)">
								<v-icon small>
									{{ isExpanded(item.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
								</v-icon>
							</v-btn>
						</div>
						<div class="bi-cell bi-cell--code">
							<div class="bi-label">Billable</div>
							<div class="bi-value font-weight-medium align-center text-truncate" :title="item.code">{{ item.code }}</div>
						</div>
						<div class="bi-cell bi-cell--project">
							<div class="bi-label">Project</div>
							<div class="bi-value">
								<div class="project-col d-flex align-center">
									<template v-if="projectList(item).length">
										<div
											class="project"
											:class="{ 'project--compact': projectList(item).length > 1 }"
											:style="{ background: projectStyle(projectList(item)[0]).lightColor }"
										>
											<div
												class="letter"
												:style="{ background: projectStyle(projectList(item)[0]).color }"
											>
												{{ projectStyle(projectList(item)[0]).small }}
											</div>
											<div class="name">
												{{ truncateProjectName(projectList(item)[0].name, projectList(item).length > 1) }}
											</div>
										</div>
										<v-tooltip v-if="projectList(item).length > 1" bottom max-width="320" z-index="300">
											<template #activator="{ on, attrs }">
												<span
													v-bind="attrs"
													v-on="on"
													class="project-more-badge ml-1 cursor-pointer"
												>
													+{{ projectList(item).length - 1 }}
												</span>
											</template>
											<div class="projects-tooltip pa-2">
												<div class="caption font-weight-bold mb-2">Projects</div>
												<div
													v-for="project in projectList(item)"
													:key="project.id"
													class="project project--tooltip mb-1"
													:style="{ background: projectStyle(project).lightColor }"
												>
													<div
														class="letter"
														:style="{ background: projectStyle(project).color }"
													>
														{{ projectStyle(project).small }}
													</div>
													<div class="name">{{ project.name }}</div>
												</div>
											</div>
										</v-tooltip>
									</template>
									<span v-else class="grey--text">—</span>
								</div>
							</div>
						</div>
						<div class="bi-cell bi-cell--date">
							<div class="bi-label">Start Date</div>
							<div class="bi-value align-center">{{ item.startDate | formatDate }}</div>
						</div>
						<div class="bi-cell bi-cell--type">
							<div class="bi-label">Type</div>
							<div class="bi-value align-center">{{ codeByType[item.type] }}</div>
						</div>
						<div class="bi-cell bi-cell--status">
							<div class="bi-label">Status</div>
							<div class="bi-value align-center">
								<v-menu offset-x right open-on-hover>
									<template #activator="{ on }">
										<v-btn
											v-on="on"
											plain small class="rounded-xl status-chip"
											:color="item.statusColor"
											:style="{ background: item.statusLightColor }"
										>{{ statusLabel(item) }}</v-btn>
									</template>
									<v-list dense>
										<v-list-item
											v-for="approver in (item.approvers || []).filter(x => x.statusToApprove === 1)"
											:key="approver.id"
										>
											<div
												class="d-flex justify-space-between align-center"
												style="min-width: 20px; width: 100%"
											>
												<span class="mr-2 body-2">
													<user-avatar :user-id="approver.approverId" :size="28" :show-card="false" />
													{{ approver.approverBlueTag }}
												</span>
												<span :set="status = approverStatus.find(s => s.id == approver.status)">
													<v-chip
														v-if="status"
														:color="status.lightColor"
														:style="{ color: status.color }"
														small
													>
														{{ status.description }}
													</v-chip>
												</span>
											</div>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</div>
						<div class="bi-cell bi-cell--currency">
							<div class="bi-label">Currency</div>
							<div class="bi-value d-flex align-center">
								<v-avatar size="28" class="mr-1">
									<v-img :src="require(`../../assets/currencies/${(item.currency || 'USD').toLowerCase()}.png`)" />
								</v-avatar>
								<span class="font-weight-medium">{{ item.currency }}</span>
							</div>
						</div>
						<div class="bi-cell bi-cell--metric">
							<div class="bi-label">Contracted</div>
							<div class="bi-value">
								<div class="metric-col">
									<template v-if="item.isOpen">
										<div class="metric-main">—</div>
										<div class="metric-sub text--secondary">Open</div>
									</template>
									<template v-else>
										<div class="metric-main font-weight-bold">{{ formatMoney(item.subtotalWithDiscount) }}</div>
										<div v-if="item.contractedHours > 0" class="metric-sub text--secondary">
											{{ formatHours(item.contractedHours) }} hrs
										</div>
									</template>
								</div>
							</div>
						</div>
						<div class="bi-cell bi-cell--metric">
							<div class="bi-label">
								<v-tooltip bottom z-index="300">
									<template #activator="{ on, attrs }">
										<span v-bind="attrs" v-on="on" class="d-inline-flex align-center">
											Assigned
											<v-icon x-small class="ml-1">mdi-information-outline</v-icon>
										</span>
									</template>
									<span>Sum of Billable Assigned Hours</span>
								</v-tooltip>
							</div>
							<div class="bi-value">
								<div v-if="!hoursMetricsApply(item)" class="metric-main">—</div>
								<div v-else class="metric-col" :class="assignedTrafficClass(item)">
									<div class="metric-main font-weight-bold">{{ assignedPct(item) }}</div>
									<div class="metric-sub">{{ formatHours(assignedHours(item)) }} hrs</div>
								</div>
							</div>
						</div>
						<div class="bi-cell bi-cell--metric">
							<div class="bi-label">
								<v-tooltip bottom z-index="300">
									<template #activator="{ on, attrs }">
										<span v-bind="attrs" v-on="on" class="d-inline-flex align-center">
											Executed
											<v-icon x-small class="ml-1">mdi-information-outline</v-icon>
										</span>
									</template>
									<span>Sum of Billable Executed Hours</span>
								</v-tooltip>
							</div>
							<div class="bi-value">
								<div v-if="!hoursMetricsApply(item)" class="metric-main">—</div>
								<div v-else class="metric-col">
									<div class="metric-main font-weight-bold">{{ executedPct(item) }}</div>
									<div class="metric-sub">{{ formatHours(executedHours(item)) }} hrs</div>
								</div>
							</div>
						</div>
						<div class="bi-cell bi-cell--metric">
							<div class="bi-label">Billed</div>
							<div class="bi-value">
								<div v-if="item.isOpen || !item.subtotalWithDiscount" class="metric-main">—</div>
								<div v-else class="metric-col">
									<div class="metric-main font-weight-bold">{{ billedPct(item) }}</div>
									<div class="metric-sub">{{ formatMoney(netBilled(item), item.currency) }}</div>
								</div>
							</div>
						</div>
						<div class="bi-cell bi-cell--actions" @click.stop>
							<div class="bi-label">Actions</div>
							<div class="bi-value bi-actions">
								<v-menu v-if="$security.can('edit')" left offset-x>
									<template #activator="{ on }">
										<v-btn v-on="on" color="primary" icon x-small>
											<v-icon size="20">mdi-dots-vertical</v-icon>
										</v-btn>
									</template>
									<v-list dense class="py-1">
										<v-list-item v-if="item.canApprove" class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="validateBillable(item)" color="primary" text small>
														<v-icon>mdi-check</v-icon>
													</v-btn>
												</template>
												<span class="caption">Validate</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="$adminSurf('openHistory', item, 'billable')" text small>
														<v-icon>mdi-history</v-icon>
													</v-btn>
												</template>
												<span class="caption">History</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="duplicate(item.id)" text small color="grey">
														<v-icon>mdi-content-copy</v-icon>
													</v-btn>
												</template>
												<span class="caption">Duplicate</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item v-if="!item.isCancelled && item.status != 6" class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="$adminSurf('closeBillable', item)" text small color="secondary">
														<v-icon>mdi-door</v-icon>
													</v-btn>
												</template>
												<span class="caption">Close</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item v-if="!item.isCancelled && item.status == 6 && $security.can('full_access')" class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="$adminSurf('openBillable', item)" text small color="secondary">
														<v-icon>mdi-door-open</v-icon>
													</v-btn>
												</template>
												<span class="caption">Reopen</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item v-if="!item.isCancelled && item.status != 6" class="pa-1 py-0">
											<v-tooltip right z-index="200">
												<template #activator="{ on }">
													<v-btn v-on="on" @click="cancelBillable(item)" text small color="error">
														<v-icon>mdi-cancel</v-icon>
													</v-btn>
												</template>
												<span class="caption">Cancel</span>
											</v-tooltip>
										</v-list-item>
										<v-list-item class="pa-1 py-0">
											<watchers-menu
												@watcher-added="item.subscribers.push($event)"
												@watcher-removed="item.subscribers.splice(item.subscribers.findIndex(x => x.userId == $event), 1)"
												:entity-id="item.id"
												:watchers="item.subscribers"
												required-permission="billables"
											/>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</div>
					</div>
					<billable-item-nest-panel
						v-if="isExpanded(item.id)"
						:items="itemsForBillable(item.id)"
						:approver-status="approverStatus"
						:current-user="currentUser"
						@refresh="fetchBillableItems"
					/>
					</div>
				</div>
			</div>
		</div>

		<div
			v-if="filteredBillables.length"
			class="bi-pagination d-flex align-center justify-end"
		>
			<div class="d-flex align-center mr-5 gap-1 nobr caption">
				Page: <b>{{ pagination.page }} / {{ paginationPages }}</b>
			</div>
			<div class="d-flex align-center mr-5 gap-1 nobr caption">
				Rows per page:
				<v-select
					v-model="pagination.itemsPerPage"
					:items="pageSizeOptions"
					dense
					hide-details
					class="bi-pagination__size caption"
				/>
			</div>
			<v-pagination
				v-model="pagination.page"
				:length="paginationPages"
				:total-visible="0"
			/>
		</div>
	</div>
</template>

<script>
import BillableJSON from '../../json/billable.json'
import WatchersMenu from '../../components/admin-surf/WatchersMenu'
import BillableItemNestPanel from '../../components/admin-surf/billable-item/BillableItemNestPanel.vue'
import BillablesNestToolbar from '../../components/admin-surf/billables-nest/BillablesNestToolbar.vue'
import moment from 'moment'

const DEFAULT_BILLABLE_STATUSES = [0, 1, 2, 3, 4, 5]

export default {
	components: { WatchersMenu, BillableItemNestPanel, BillablesNestToolbar },
	props: ['customerId', 'currency'],
	data(){
		return {
			billables: [],
			billableItems: [],
			expandedBillableIds: {},
			holidays: [],
			filterMenuProps: {
				offsetY: true,
				bottom: true,
				nudgeBottom: 4,
				allowOverflow: true,
				closeOnContentClick: false,
				contentClass: 'bnt-filter-menu',
			},
			openBillables: [
				{ value: true, text: 'Open billable' },
				{ value: false, text: 'Without open billable' },
			],
			filters: {
				search: '',
				status: DEFAULT_BILLABLE_STATUSES.slice(),
				customer: [],
				project: [],
				currency: [],
				type: [],
				openBillable: null,
				billableCode: [],
				billableItemCode: [],
				billableItemStatus: [],
			},
			searchTimeout: null,
			statuses: [],
			billableItemStatuses: [],
			currencies: [
				{text: 'MXN'},
				{text: 'USD'},
				{text: 'EUR'},
			],
			customers: [],
			projects: [],
			projectTypes: [],
			codeByType: ['FxR', 'RPAH', 'RPEH', 'Int'],
			approverStatus: [],
			defaultProjectType: { color: '#757575', lightColor: '#eeeeee', small: '?' },
			types: [
				{ text: 'Fixed Rate', value: 0, },
				{ text: 'Rate per Assigned Hour', value: 1, },
				{ text: 'Rate per Executed Hour', value: 2, },
				{ text: 'Internal', value: 3, },
			],
			currentUser: {},
			pageSizeOptions: [50, 100, 'All'],
			pagination: {
				page: 1,
				itemsPerPage: 50,
				totalItems: 0,
				sortBy: '',
				sortDesc: false,
			},
		}
	},
	watch: {
		'pagination.itemsPerPage'() {
			this.pagination.page = 1
		},
		filters: {
			deep: true,
			handler() {
				this.pagination.page = 1
			},
		},
		filteredBillables() {
			if (this.pagination.page > this.paginationPages) {
				this.pagination.page = 1
			}
		},
	},
	created(){
		document.title = 'Billables'
		this.setUserPreferences("Billables", "filters")
		if (!this.filters.status || !this.filters.status.length) {
			this.filters.status = DEFAULT_BILLABLE_STATUSES.slice()
		}
		this.$q.log(4, 23, 3, 'NA')
		this.projectTypes = BillableJSON.projectTypes

		if(this.customerId){
			this.filters = {
				search: '',
				status: DEFAULT_BILLABLE_STATUSES.slice(),
				customer: [],
				project: [],
				currency: [],
				type: [],
				openBillable: null,
				billableCode: [],
				billableItemCode: [],
				billableItemStatus: [],
			}
			if(this.currency)
				this.filters.currency.push(this.currency)
			this.filters.customer.push(this.customerId)
		}

		this.init()
	},
	computed: {
		statusSelectionLabel(){
			const selected = this.filters.status || []
			if (!selected.length) return ''
			if (this.statuses.length && selected.length >= this.statuses.length) return 'All statuses'
			if (selected.length === 1) {
				const match = this.statuses.find(s => s.id === selected[0])
				return match ? match.description : '1 selected'
			}
			return `${selected.length} selected`
		},
		billableItemStatusSelectionLabel(){
			const selected = this.filters.billableItemStatus || []
			if (!selected.length) return ''
			if (this.billableItemStatuses.length && selected.length >= this.billableItemStatuses.length) return 'All statuses'
			if (selected.length === 1) {
				const match = this.billableItemStatuses.find(s => s.id === selected[0])
				return match ? match.description : '1 selected'
			}
			return `${selected.length} selected`
		},
		hasBillableItemFilters(){
			return !!(
				(this.filters.billableItemCode && this.filters.billableItemCode.length) ||
				(this.filters.billableItemStatus && this.filters.billableItemStatus.length)
			)
		},
		filteredBillables(){
			let list = this.billables || []
			if (this.filters.billableCode && this.filters.billableCode.length) {
				list = list.filter(b => this.filters.billableCode.includes(b.id))
			}
			if (this.hasBillableItemFilters) {
				list = list.filter(b => this.itemsForBillable(b.id).length > 0)
			}
			return list
		},
		appliedFilters(){
			return this.countActiveFilters(this.filters, ['search'])
		},
		paginationPages(){
			if (this.pagination.itemsPerPage === 'All') return 1
			const size = Number(this.pagination.itemsPerPage) || 50
			return Math.ceil(this.filteredBillables.length / size) || 1
		},
		orderedBillables(){
			const assignedMap = {}
			const unassignedMap = {}

			this.filteredBillables.forEach(item => {
				const target = this.hasDeliveryManager(item) ? assignedMap : unassignedMap
				const key = item.customerId || 'unknown'
				if (!target[key]) {
					target[key] = {
						key,
						name: item.customerName || 'Unknown',
						items: [],
					}
				}
				target[key].items.push(item)
			})

			const sortGroups = map => Object.values(map)
				.sort((a, b) => a.name.localeCompare(b.name))

			const ordered = []
			sortGroups(assignedMap).forEach(group => ordered.push(...group.items))
			sortGroups(unassignedMap).forEach(group => ordered.push(...group.items))
			return ordered
		},
		pagedBillables(){
			const list = this.orderedBillables
			if (this.pagination.itemsPerPage === 'All') return list
			const size = Number(this.pagination.itemsPerPage) || 50
			const start = (this.pagination.page - 1) * size
			return list.slice(start, start + size)
		},
		customerGroups(){
			const assignedMap = {}
			const unassignedMap = {}

			this.pagedBillables.forEach(item => {
				const target = this.hasDeliveryManager(item) ? assignedMap : unassignedMap
				const key = item.customerId || 'unknown'
				if(!target[key]){
					target[key] = {
						key,
						name: item.customerName || 'Unknown',
						items: [],
					}
				}
				target[key].items.push(item)
			})

			const sortGroups = map => Object.values(map)
				.sort((a, b) => a.name.localeCompare(b.name))

			return {
				assigned: sortGroups(assignedMap),
				unassigned: sortGroups(unassignedMap),
			}
		},
		listSections(){
			const sections = []
			if(this.customerGroups.assigned.length){
				sections.push({
					key: 'assigned',
					title: null,
					isUnassigned: false,
					groups: this.customerGroups.assigned,
				})
			}
			if(this.customerGroups.unassigned.length){
				sections.push({
					key: 'unassigned',
					title: 'Unassigned Customers',
					isUnassigned: true,
					groups: this.customerGroups.unassigned,
				})
			}
			return sections
		},
	},
	methods: {
		multiSelectionLabel(values, kind){
			const selected = values || []
			if (!selected.length) return ''
			if (selected.length === 1) {
				if (kind === 'customer') {
					const match = this.customers.find(c => c.id === selected[0])
					return match ? match.name : '1 selected'
				}
				if (kind === 'project') {
					const match = this.projects.find(p => p.id === selected[0])
					return match ? match.name : '1 selected'
				}
				if (kind === 'currency') return selected[0]
				if (kind === 'type') {
					const match = this.types.find(t => t.value === selected[0])
					return match ? match.text : '1 selected'
				}
				if (kind === 'billableCode') {
					const match = this.billables.find(b => b.id === selected[0])
					return match ? match.code : '1 selected'
				}
				if (kind === 'billableItemCode') {
					const match = this.billableItems.find(b => b.id === selected[0])
					return match ? match.code : '1 selected'
				}
				return '1 selected'
			}
			return `${selected.length} selected`
		},
		hasDeliveryManager(item){
			return (item.customerProjects || []).some(p => p.deliveryManagerId)
		},
		projectList(item){
			return item.customerProjects || []
		},
		truncateProjectName(name, compact){
			const n = (name || '').trim()
			if(compact){
				const max = 7
				if(n.length <= max) return n
				return `${n.slice(0, max)}...`
			}
			if(n.length <= 14) return n
			return `${n.slice(0, 14)}...`
		},
		projectStyle(project){
			const t = this.projectTypes.find(pt => pt.value == project.type)
			return t || this.defaultProjectType
		},
		statusLabel(item){
			const name = (item.statusDisplayName || '').trim()
			if(/^to be assigned$/i.test(name)) return 'Not Assigned'
			return name
		},
		formatHours(n){
			return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)
		},
		formatMoney(amount){
			const cur = 'USD'
			try {
				return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(amount || 0)
			} catch {
				return `$${(amount || 0).toFixed(2)}`
			}
		},
		formatPct(num, den){
			if(!den || den <= 0) return '—'
			const p = (num / den) * 100
			return `${Math.round(p)}%`
		},
		hoursMetricsApply(item){
			return item.type !== 0 && item.type !== 2
		},
		assignedHours(item){
			return (item.plannedBillableHours || 0)
		},
		executedHours(item){
			return (item.executedBillableHours || 0)
		},
		hourDenominator(item){
			if(item.isOpen) return 0
			const ch = item.contractedHours || 0
			if(ch > 0) return ch
			return 0
		},
		assignedPct(item){
			const den = this.hourDenominator(item)
			if(den > 0) return this.formatPct(this.assignedHours(item), den)
			const h = this.assignedHours(item)
			return h > 0 ? '—' : '0%'
		},
		assignedTrafficClass(item){
			const den = this.hourDenominator(item)
			const hrs = this.assignedHours(item)
			if(den > 0){
				const p = (hrs / den) * 100
				if(p >= 98) return 'assigned-traffic assigned-traffic--good'
				if(p >= 75) return 'assigned-traffic assigned-traffic--warn'
				return 'assigned-traffic assigned-traffic--bad'
			}
			if(hrs > 0) return 'assigned-traffic assigned-traffic--muted'
			return 'assigned-traffic assigned-traffic--bad'
		},
		executedPct(item){
			const den = this.hourDenominator(item)
			if(den > 0) return this.formatPct(this.executedHours(item), den)
			const h = this.executedHours(item)
			return h > 0 ? '—' : '0%'
		},
		netBilled(item){
			return (item.billed || 0) + (item.positiveAdjustment || 0) + (item.negativeAdjustment || 0)
		},
		billedPct(item){
			const sub = item.subtotalWithDiscount || 0
			if(sub <= 0) return '—'
			return this.formatPct(this.netBilled(item), sub)
		},
		async init(){
			await this.$blueSystem.getCurrentUser()
				.then(user => {
					this.currentUser = user
				})
				.catch(() => this.$root.error('Failed to get user'))

			this.$http.get("/api/customer?onlyWithCustomerProjects=true")
				.then(res => {
					this.customers = _.orderBy(res.data, ['name'])

					for(let customer of this.customers){
						for(let project of customer.customerProjects){
							if(!this.projects.some(p => p.id == project.id)){
								this.projects.push(project)
							}
						}
					}
					this.projects = _.orderBy(this.projects, ['name'])
				})
				.catch(res => {
					console.error(res)
					window.getApp.error("Cannot obtain customers.")
				})

			this.$http.get('api/enums/admin/billable/status')
				.then(res => {
					this.statuses = res.data
				})
				.catch(res => {
					console.error(res)
					window.getApp.error("Cannot obtain billable statuses.")
				})

			this.$http.get('api/enums/admin/billableItem/status')
				.then(res => {
					this.billableItemStatuses = res.data || []
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Cannot obtain billable item statuses')
				})

			this.$http.get('api/enums/approver/status')
				.then(res => {
					this.approverStatus = res.data
				})
				.catch(err => {
					console.error(err)
				})

			this.fetchBillables()
			this.fetchBillableItems()
		},
		fetchBillableItems(){
			this.$http.get('api/billableItem?includeDeleted=true')
				.then(res => {
					this.billableItems = (res.data || [])
						.map(x => ({
							...x,
							approvers: x.approvers || [],
							subscribers: x.subscribers || [],
							canApprove: (x.approvers || [])
								.some(y =>
									!x.isDraft && !x.reviewed &&
									(y.approverId == this.currentUser.id || this.currentUser.isSuperAdmin) &&
									y.statusToApprove === 2 &&
									y.status === 0
								)
						}))
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Cannot obtain billable items')
				})
		},
		toggleExpand(billableId){
			this.$set(this.expandedBillableIds, billableId, !this.expandedBillableIds[billableId])
		},
		isExpanded(billableId){
			return !!this.expandedBillableIds[billableId]
		},
		itemsForBillable(billableId){
			return this.billableItems.filter(i => {
				if (i.billableId !== billableId) return false
				if (this.filters.billableItemCode && this.filters.billableItemCode.length
					&& !this.filters.billableItemCode.includes(i.id)) {
					return false
				}
				if (this.filters.billableItemStatus && this.filters.billableItemStatus.length
					&& !this.filters.billableItemStatus.includes(i.status)) {
					return false
				}
				return true
			})
		},
		fetchBillables(){
			let sortBy = Array.isArray(this.pagination.sortBy) ? this.pagination.sortBy[0] : this.pagination.sortBy
			let sortDesc = Array.isArray(this.pagination.sortDesc) ? this.pagination.sortDesc[0] : this.pagination.sortDesc

			this.$http.post(
				'api/billable/detailed',
				{
					search: this.filters.search,
					status: this.filters.status,
					customerId: this.filters.customer,
					customerProjectId: this.filters.project,
					currency: this.filters.currency,
					type: this.filters.type,
					openBillable: this.filters.openBillable,
					compress: false,

					page: -1,
					pageSize: -1,
					sortBy: sortBy,
					sortDesc: sortDesc,
				}
			)
				.then(res => {
					this.billables = res.data.billables
						.map(x => ({
							...x,
							canApprove: x.approvers
								.some(y =>
									!x.isDraft && !x.isApproved && !x.isCancelled &&
									y.approverId == this.currentUser.id &&
									y.statusToApprove === 1 &&
									y.status === 0
								)
						}))
					this.pagination.totalItems = res.data.total

					if(this.billables.length == 0 && this.pagination.totalItems){
						this.pagination.page = 1
					}
				})
				.catch(res => {
					console.error(res)
					this.$root.error("Cannot obtain billables.")
				})
		},
		handleSearch(){
			clearTimeout(this.searchTimeout)
			this.searchTimeout = setTimeout(this.fetchBillables, 500)
		},
		duplicate(id){
			this.$router.push({ path: '/admin/billable/new', query: { billableBase: id }})
		},
		changeCustomer(){
			this.filters.project = null
			this.fetchBillables()
		},
		cancelBillable(item){
			this.$adminSurf('cancelBillable', item.id)
				.then(res => {
					if(res){
						this.fetchBillables()
					}
				})
				.catch(res => {
					console.log(res)
					this.$root.error('Cannot delete billable')
				})
		},
		validateBillable(item){
			this.$adminSurf('validateBillable', item)
				.then(() => {
					this.fetchBillables()
					this.$root.success('Billable marked as approved')
				})
				.catch(res => {
					console.log(res)
					this.$root.error('Cannot validate billable')
				})
		},
		async onExcelExport(type) {
			if (type === 'billableItems') {
				this.generateBillableItemsExcel()
				return
			}
			await this.generateExcel()
		},
		async generateExcel() {
			const sortBy = Array.isArray(this.pagination.sortBy) ? this.pagination.sortBy[0] : this.pagination.sortBy
			const sortDesc = Array.isArray(this.pagination.sortDesc) ? this.pagination.sortDesc[0] : this.pagination.sortDesc

			const res = await this.$http.post(
				'api/billable/detailed',
				{
					search: this.filters.search,
					status: this.filters.status,
					customerId: this.filters.customer,
					customerProjectId: this.filters.project,
					currency: this.filters.currency,
					type: this.filters.type,
					openBillable: this.filters.openBillable,
					compress: false,

					page: -1,
					sortBy: sortBy,
					sortDesc: sortDesc,
				}
			)
			const billables = res.data.billables

			const excelHeaders = [
				{ name: 'Billable', value: x => x.code },
				{ name: 'Customer', value: x => x.customerName || '' },
				{ name: 'Projects', value: x => x.customerProjects.map(p => p.name).join(', ') },
				{ name: 'Delivery Managers', value: x => {
					let deliveryManagers = x.customerProjects.map(p => p.deliveryManagerBlueTag)
					deliveryManagers = [...new Set(deliveryManagers)]
					return deliveryManagers.join(', ')
				} },
				{ name: 'Currency', value: x => x.currency || '' },
				{ name: 'Type', value: x => this.types.find(y => y.value == x.type).text },
				{ name: 'Start Date', value: x => x.startDate ? moment(x.startDate).format('MMM D, YYYY') : '-' },
				{ name: 'Status', value: x => x.statusDisplayName },
				{ name: 'Planned Hours', value: x => x.plannedHours || '-' },
				{ name: 'Contracted Hours', value: x => x.contractedHours || '-' },
				{ name: 'Executed Hours', value: x => x.executedHours || '-' },
				{ name: 'Billed Amount', value: x => x.billed || '-' },
				{ name: 'Contracted Amount', value: x => x.totalRate || '-' },
				{ name: 'Created By', value: x => x.createdByBlueTag || x.createdByFullName || '-' },
				{ name: 'Created At', value: x => x.createdAt ? moment(x.createdAt).format('MMM D, YYYY') : '-' },
			]
			this.singleSheetExcel(billables, excelHeaders, 'Billables')
		},
		generateBillableItemsExcel() {
			const items = this.filteredBillables
				.flatMap(billable => this.itemsForBillable(billable.id))

			const excelHeaders = [
				{ name: 'Code', value: x => x.code || '' },
				{ name: 'Billable', value: x => x.billableCode || '' },
				{ name: 'Customer', value: x => x.customerName || '' },
				{ name: 'Start Date', value: x => x.startDate ? moment(x.startDate).format('DD/MM/YYYY') : '' },
				{ name: 'End Date', value: x => x.endDate ? moment(x.endDate).format('DD/MM/YYYY') : '' },
				{ name: 'Currency', value: x => x.billableCurrency || '' },
				{ name: 'Status', value: x => x.statusDisplayName || '' },
				{ name: 'Hours', value: x => x.hours || '' },
				{ name: 'Closed', value: x => x.isClosed ? 'Yes' : 'No' },
				{ name: 'Billed', value: x => x.total || '' },
				{ name: 'Invoiced', value: x => x.invoiced || '0' },
				{ name: 'Adjustment', value: x => x.adjustmentAmount || '-' },
				{ name: 'Created By', value: x => x.createdByBlueTag || x.createdByFullName || '-' },
				{ name: 'Created At', value: x => x.createdAt ? moment(x.createdAt).format('DD/MM/YYYY') : '-' },
			]
			this.singleSheetExcel(items, excelHeaders, 'Billable Items')
		},
	},
}
</script>

<style lang="scss" scoped>
.bnt-add-billable {
	border-radius: 10px !important;
	height: 40px !important;
	padding: 0 14px !important;
	font-weight: 600;
	letter-spacing: 0;
	box-shadow: none !important;
}

.bi-pagination {
	margin-top: 8px;
	padding: 8px 0 4px;
	gap: 4px;

	&__size {
		width: 72px;
		margin: 0;
		padding: 0;

		::v-deep .v-input__control {
			min-height: 28px !important;
		}

		::v-deep .v-input__slot {
			min-height: 28px !important;
			margin: 0 !important;
		}

		::v-deep .v-select__selections {
			font-size: 0.75rem;
		}
	}
}

::v-deep .bi-pagination .v-pagination__navigation {
	box-shadow: none !important;
	margin: 0 2px;
}

.bnt-status-item {
	min-height: 40px !important;
}

.bi-section {
	&--unassigned {
		margin-top: 8px;
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 10px;
		margin: 8px 0 14px;
	}

	&__title {
		font-size: 0.95rem;
		font-weight: 500;
		color: rgba(0, 0, 0, 0.45);
		white-space: nowrap;
	}

	&__icon {
		flex-shrink: 0;
	}

	&__line {
		flex: 1;
		height: 1px;
		background: rgba(0, 0, 0, 0.12);
	}
}

.bi-billable-block {
	& + .bi-billable-block {
		border-top: 1px solid var(--v-newDesignDivider-base, #e4e4e4);
	}
}

.customer-group {
	overflow: hidden;
	background: var(--v-newDesignBackground-base);
	box-shadow: none;

	&__header {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 12px 16px 8px;
		border-bottom: none;
	}

	&__title {
		font-weight: 700;
		font-size: 1.05rem;
		line-height: 1.2;
		color: var(--v-newDesignText-base);
	}

	&__body {
		padding: 8px 0;
	}
}

.bi-row {
	display: grid;
	grid-template-columns:
		28px
		minmax(110px, 1.1fr)
		minmax(140px, 1.3fr)
		minmax(90px, 0.85fr)
		minmax(56px, 0.55fr)
		minmax(96px, 0.9fr)
		minmax(80px, 0.75fr)
		minmax(90px, 0.85fr)
		minmax(90px, 0.85fr)
		minmax(90px, 0.85fr)
		minmax(90px, 0.85fr)
		28px;
	gap: 6px;
	align-items: end;
	padding: 6px 16px;
	border-radius: 0;
	border: none;
	box-shadow: none;

	& + .bi-row {
		border-top: 1px solid var(--v-newDesignDivider-base, #e4e4e4);
	}

	&--clickable {
		cursor: pointer;

		&:hover {
			background: rgba(0, 0, 0, 0.02);
		}
	}
}

.bi-cell--expand {
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: stretch;
	min-height: 34.5px;

	.v-btn {
		margin: 0;
		height: 28px !important;
		width: 28px !important;
	}
}

.bi-header .bi-cell--expand {
	min-height: 0;
}

.bi-cell {
	min-width: 0;
}

.bi-label {
	display: none;
}

.bi-header {
	align-items: center;
	padding-top: 2px;
	padding-bottom: 6px;

	&__cell {
		font-size: 0.78rem;
		font-weight: 500;
		color: var(--v-newDesignText-primary);
		opacity: 0.65;
		white-space: nowrap;
		line-height: 1.2;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__cell--actions {
		text-align: right;
	}
}

.bi-value {
	font-size: 0.85rem;
	color: var(--v-newDesignText-base);
	line-height: 1.25;
	word-break: break-word;
	min-height: 34.5px;
	display: flex;
	align-items: flex-end;
}

.bi-cell--project {
	.bi-value {
		align-items: center;
	}

	.project-col {
		min-height: 34.5px;
	}
}

.bi-cell--actions {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.bi-label {
		text-align: right;
	}

	.bi-value {
		justify-content: flex-end;
	}
}

.bi-actions {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	min-height: 34.5px;
}

.status-chip {
	justify-content: center;
	width: 88px;
	min-width: 88px;
	max-width: 88px;
	height: 24px;
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0;
	text-transform: none;

	::v-deep .v-btn__content {
		opacity: 1 !important;
	}
}

.metric-col {
	line-height: 1.25;
	min-height: 34.5px;
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
}

.metric-main {
	font-size: 14px;
	line-height: 1.25;
}

.metric-sub {
	font-size: 12px;
	line-height: 1.25;
	min-height: 15px;
	margin-top: 2px;
}

.assigned-traffic--good .metric-main,
.assigned-traffic--good .metric-sub {
	color: #2e7d32 !important;
}
.theme--dark .assigned-traffic--good .metric-main,
.theme--dark .assigned-traffic--good .metric-sub {
	color: #81c784 !important;
}
.assigned-traffic--warn .metric-main,
.assigned-traffic--warn .metric-sub {
	color: #f57f17 !important;
}
.theme--dark .assigned-traffic--warn .metric-main,
.theme--dark .assigned-traffic--warn .metric-sub {
	color: #ffca28 !important;
}
.assigned-traffic--bad .metric-main,
.assigned-traffic--bad .metric-sub {
	color: #c62828 !important;
}
.theme--dark .assigned-traffic--bad .metric-main,
.theme--dark .assigned-traffic--bad .metric-sub {
	color: #ef5350 !important;
}
.assigned-traffic--muted .metric-main,
.assigned-traffic--muted .metric-sub {
	color: rgba(0, 0, 0, 0.54) !important;
}
.theme--dark .assigned-traffic--muted .metric-main,
.theme--dark .assigned-traffic--muted .metric-sub {
	color: rgba(255, 255, 255, 0.5) !important;
}

.project {
	display: inline-flex;
	align-items: center;
	border-radius: 15px;
	margin-right: 5px;
	white-space: nowrap;
	height: 28px;

	div {
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.letter {
		width: 28px;
		height: 28px;
		padding: 5px;
		color: white;
		border-radius: 15px;
		flex-shrink: 0;
	}

	.name {
		padding: 0 10px 0 5px;
		color: black;
	}
}

.project--tooltip {
	display: flex;
	width: 100%;
}

.projects-tooltip {
	max-height: 280px;
	overflow-y: auto;
}

.project-col {
	width: 100%;
	max-width: 200px;
	min-width: 0;
	overflow: hidden;
	flex-wrap: nowrap;
}

.project--compact .name {
	max-width: 72px;
	padding-right: 6px;
	overflow: hidden;
}

.project:not(.project--compact) .name {
	max-width: 132px;
	padding-right: 8px;
	overflow: hidden;
}

.project-more-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 28px;
	height: 28px;
	padding: 0 4px;
	border-radius: 50%;
	background: var(--v-primary-base);
	color: #fff !important;
	font-size: 11px;
	font-weight: 700;
	flex-shrink: 0;
	line-height: 1;
}

.cursor-pointer {
	cursor: pointer;
}
</style>
